import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Divider,
} from "@mui/material";
import { useGetProductsQuery } from "../../../app/services/product";
import {
  useGetProductSubscriptionsQuery,
  useGetSubscriptionFeaturesQuery,
} from "../../../app/services/subscription";
import { useAssignProductV2Mutation, useUnAssignProductV2Mutation } from "../../../app/services/dealer";
import type { ProductSubscriptionsDto, ProductSubscription } from "../../../app/interfaces/subscription";
import {
  useGetProductAddOnsQuery,
  useGetDealerActiveAddOnsQuery,
  useAssignAddOnMutation,
  useUnassignAddOnMutation,
} from "../../../app/services/addons";

interface OnboardProductDialogProps {
  open: boolean;
  onClose: () => void;
  dealerId?: number | null;
  dealerName?: string;
  // Products already assigned to dealer, to exclude from select
  assignedProductIds?: string[];
  // Callbacks to refresh data in parent
  onSuccess?: () => void;
  // Optional: preselect product & subscription (edit mode)
  initialSelectedProductId?: string;
  initialSelectedSubscriptionId?: string;
  // If true, product dropdown is disabled (edit mode)
  lockProductSelection?: boolean;
  // UI mode
  mode?: 'create' | 'edit';
}

const OnboardProductDialog: React.FC<OnboardProductDialogProps> = ({
  open,
  onClose,
  dealerId,
  dealerName,
  assignedProductIds = [],
  onSuccess,
  initialSelectedProductId,
  initialSelectedSubscriptionId,
  lockProductSelection = false,
  mode = 'create',
}) => {
  const [selectedProductId, setSelectedProductId] = React.useState<string>("");
  const [selectedSubscriptionId, setSelectedSubscriptionId] = React.useState<string>("");
  const [errorText, setErrorText] = React.useState<string>("");

  const { data: productsResp, isFetching: productsLoading } = useGetProductsQuery(
    { page: 1, pageSize: 500 },
    { skip: !open }
  );

  const { data: allSubscriptions, isFetching: subsLoading } = useGetProductSubscriptionsQuery(undefined, { skip: !open });

  const { data: features, isFetching: featuresLoading } = useGetSubscriptionFeaturesQuery(selectedSubscriptionId, {
    skip: !open || !selectedSubscriptionId,
  });

  const [assignProductV2, { isLoading: assigning } ] = useAssignProductV2Mutation();

  // Add-ons
  const { data: addOns, isFetching: addOnsLoading } = useGetProductAddOnsQuery(selectedProductId, {
    skip: !open || !selectedProductId,
  });
  const { data: activeAddOns, refetch: refetchActiveAddOns } = useGetDealerActiveAddOnsQuery(
    { dealerId: dealerId ?? -1, productId: selectedProductId },
    { skip: !open || !selectedProductId || !dealerId }
  );
  const [assignAddOn] = useAssignAddOnMutation();
  const [unassignAddOn] = useUnassignAddOnMutation();
  const [unassignProductV2, { isLoading: ending }] = useUnAssignProductV2Mutation();
  const [endReason, setEndReason] = React.useState<string>("");
  const [assignReason, setAssignReason] = React.useState<string>("");

  const availableProducts = React.useMemo(() => {
    const list = (productsResp?.data ?? productsResp ?? []) as any[];
    const filtered = list.filter((p) => !assignedProductIds.includes(p.id || p.productId || p.productID));
    // In edit mode, ensure the initially selected product is present even if assigned
    if (initialSelectedProductId) {
      const exists = filtered.some((p) => String(p.id ?? p.productId) === String(initialSelectedProductId));
      if (!exists) {
        const found = list.find((p) => String(p.id ?? p.productId) === String(initialSelectedProductId));
        if (found) filtered.unshift(found);
      }
    }
    return filtered;
  }, [productsResp, assignedProductIds, initialSelectedProductId]);

  const productTiers = React.useMemo(() => {
    if (!selectedProductId || !allSubscriptions) return [] as ProductSubscription[];
    const group = (allSubscriptions as ProductSubscriptionsDto[]).find(
      (s) => String(s.productId) === String(selectedProductId)
    );
    return group?.subscriptions ?? [];
  }, [allSubscriptions, selectedProductId]);

  const resetState = () => {
    setSelectedProductId("");
    setSelectedSubscriptionId("");
    setErrorText("");
  };

  const [endConfirmOpen, setEndConfirmOpen] = React.useState(false);
  const handleEndSubscription = async () => {
    if (!dealerId || !selectedProductId) return;
    setErrorText("");
    try {
      await unassignProductV2({ dealerId: dealerId as number, productId: String(selectedProductId), reason: endReason || undefined }).unwrap();
      await refetchActiveAddOns();
      if (onSuccess) onSuccess();
      setEndConfirmOpen(false);
      handleClose();
    } catch (e: any) {
      setErrorText(e?.data ?? "Failed to end subscription.");
    }
  };

  const handleClose = () => {
    if (assigning) return;
    resetState();
    onClose();
  };

  // Pre-populate in edit mode when opened
  React.useEffect(() => {
    if (open) {
      if (initialSelectedProductId) setSelectedProductId(String(initialSelectedProductId));
      if (initialSelectedSubscriptionId) setSelectedSubscriptionId(String(initialSelectedSubscriptionId));
    } else {
      // When closed, clear transient state
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSelectedProductId, initialSelectedSubscriptionId]);

  const handleSubmit = async () => {
    setErrorText("");
    if (!dealerId) {
      setErrorText("Dealer id is missing.");
      return;
    }
    if (!selectedProductId) {
      setErrorText("Please select a product to onboard.");
      return;
    }

    try {
      await assignProductV2({
        productId: selectedProductId,
        dealerIds: [String(dealerId)],
        subscriptionId: selectedSubscriptionId || undefined,
        reason: assignReason || undefined,
      }).unwrap();
      // After product assignment, also refresh add-ons active state
      await refetchActiveAddOns();
      if (onSuccess) onSuccess();
      handleClose();
    } catch (e: any) {
      setErrorText(e?.data ?? "Failed to onboard product.");
    }
  };

  const isFeatureActive = (featureId: string): boolean => {
    const ids = activeAddOns?.activeFeatureIds || [];
    return ids.includes(String(featureId));
  };

  const toggleAddOn = async (featureId: string, enable: boolean) => {
    if (!dealerId) return;
    try {
      if (enable) {
        await assignAddOn({ productId: selectedProductId, dealerId, featureId }).unwrap();
      } else {
        await unassignAddOn({ productId: selectedProductId, dealerId, featureId }).unwrap();
      }
      await refetchActiveAddOns();
    } catch (e) {
      console.error("Failed to toggle add-on", e);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {mode === 'edit'
          ? (dealerName ? `Edit Subscription for ${dealerName}` : 'Edit Subscription')
          : (dealerName ? `Onboard ${dealerName} to Product` : "Onboard Dealer to Product")}
      </DialogTitle>
      <DialogContent dividers>
        {errorText && (
          <Alert severity="error" sx={{ mb: 2 }}>{String(errorText)}</Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            select
            label="Select Product"
            fullWidth
            value={selectedProductId}
            onChange={(e) => {
              setSelectedProductId(e.target.value);
              setSelectedSubscriptionId("");
            }}
            disabled={productsLoading || lockProductSelection}
          >
            {productsLoading ? (
              <MenuItem value="" disabled>
                Loading products...
              </MenuItem>
            ) : availableProducts.length ? (
              [
                <MenuItem key="__choose" value="">
                  Choose a product...
                </MenuItem>,
                ...availableProducts.map((p: any) => (
                  <MenuItem key={p.id ?? p.productId} value={String(p.id ?? p.productId)}>
                    {p.productName ?? p.name}
                  </MenuItem>
                )),
              ]
            ) : (
              <MenuItem value="" disabled>
                No products available
              </MenuItem>
            )}
          </TextField>

          {/* Reason for onboarding or changing tier */}
          {selectedProductId && (
            <TextField
              label={mode === 'edit' ? 'Reason for change (optional)' : 'Reason for onboarding (optional)'}
              placeholder={mode === 'edit' ? 'Why is the subscription tier changing?' : 'Why is this dealer being onboarded?'}
              fullWidth
              multiline
              minRows={2}
              value={assignReason}
              onChange={(e) => setAssignReason(e.target.value)}
            />
          )}

          <TextField
            select
            label="Select Subscription Tier"
            fullWidth
            value={selectedSubscriptionId}
            onChange={(e) => setSelectedSubscriptionId(e.target.value)}
            disabled={!selectedProductId || subsLoading}
          >
            {!selectedProductId ? (
              <MenuItem value="" disabled>
                Choose a product first
              </MenuItem>
            ) : subsLoading ? (
              <MenuItem value="" disabled>
                Loading tiers...
              </MenuItem>
            ) : productTiers.length ? (
              [
                <MenuItem key="__choose_tier" value="">
                  Choose a tier...
                </MenuItem>,
                ...productTiers.map((t: ProductSubscription) => (
                  <MenuItem key={t.productSubscriptionId} value={String(t.productSubscriptionId)}>
                    {t.subscriptionName}
                  </MenuItem>
                )),
              ]
            ) : (
              <MenuItem value="" disabled>
                No tiers found for this product
              </MenuItem>
            )}
          </TextField>

          {selectedSubscriptionId && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Included Features
              </Typography>
              {featuresLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">Loading features...</Typography>
                </Box>
              ) : (features ?? []).length ? (
                <Grid container spacing={1}>
                  {(features as any[]).map((f: any) => (
                    <Grid item key={f.productFeatureId}>
                      <Chip
                        label={
                          f.featureName ??
                          f.FeatureName ??
                          f.featureDescription ??
                          f.FeatureDescription ??
                          f.productFeatureName ??
                          f.ProductFeatureName ??
                          f.description ??
                          f.Description ??
                          'Feature'
                        }
                        variant="outlined"
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">No features found for this tier</Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Note: Selecting a tier will be stored with the dealer product and written to subscription history.
              </Typography>
            </Box>
          )}

          {/* Add-Ons Section */}
          {selectedProductId && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Available Add-Ons
              </Typography>
              {addOnsLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">Loading add-ons...</Typography>
                </Box>
              ) : (addOns ?? []).length ? (
                <Grid container spacing={1}>
                  {(addOns as AddOnCatalogItemDto[]).map((a) => (
                    <Grid item xs={12} sm={6} key={a.featureId}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isFeatureActive(a.featureId)}
                            onChange={(e) => toggleAddOn(a.featureId, e.target.checked)}
                            color="primary"
                          />
                        }
                        label={a.featureName || a.featureId}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">No add-ons defined for this product</Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Toggle add-ons to assign/unassign for this dealer. Changes are recorded in add-on history.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {mode === 'edit' ? (
          <Button color="error" variant="text" onClick={() => setEndConfirmOpen(true)} disabled={ending}>
            {ending ? <CircularProgress size={20} /> : 'End Subscription'}
          </Button>
        ) : <span />}
        <Box>
          <Button onClick={handleClose} variant="outlined" disabled={assigning || ending} sx={{ mr: 1 }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={assigning || ending || !selectedProductId}>
            {assigning ? <CircularProgress size={20} /> : (mode === 'edit' ? 'Save Changes' : 'Onboard Product')}
          </Button>
        </Box>
      </DialogActions>

      {/* Confirm End Subscription */}
      <Dialog open={endConfirmOpen} onClose={() => setEndConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>End Subscription?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will end the current subscription for this dealer. You can reassign a new tier later if needed.
          </Typography>
          <TextField
            label="Reason (optional)"
            placeholder="Enter a reason for ending the subscription"
            fullWidth
            multiline
            minRows={2}
            value={endReason}
            onChange={(e) => setEndReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndConfirmOpen(false)} variant="outlined" disabled={ending}>Cancel</Button>
          <Button onClick={handleEndSubscription} color="error" variant="contained" disabled={ending}>
            {ending ? <CircularProgress size={20} /> : 'End Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default OnboardProductDialog;
